import React, { useState } from "react";
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../Images/BizBridge-Logo-1.png";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: "Features", href: "#features", type: "scroll" },
    // { label: "Pricing", href: "#pricing", type: "scroll" },
    { label: "About", href: "/about", type: "route" },
    { label: "Contact", href: "/contact", type: "route" },
  ];

  // 🔹 Handle Navigation (Scroll + Route)
  const handleNavClick = (item) => {
    setMobileMenuOpen(false);

    if (item.type === "route") {
      navigate(item.href);
      return;
    }

    // Scroll logic (only for items with hash links)
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.querySelector(item.href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);

    // Only try to use querySelector if it's a hash link (starts with #)
    if (href.startsWith("#")) {
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // For route paths, just navigate
      navigate(href);
    }
  };

  const scrollToTop = () => {
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          width: "100%",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          zIndex: 1000,
          borderBottom: "2px solid #0B4064",
        }}
      >
        <Box
          component="nav"
          sx={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "1.2rem 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <Box
            onClick={scrollToTop}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="BizBridge Logo"
              sx={{
                height: 55,
                width: "auto",
                display: "block",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Hamburger Button */}
          <Box
            onClick={() => setMobileMenuOpen(true)}
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              cursor: "pointer",
              zIndex: 1001,
              gap: "5px",
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                component="span"
                sx={{
                  display: "block",
                  width: "28px",
                  height: "4px",
                  backgroundColor: "#0B4064",
                  borderRadius: "3px",
                }}
              />
            ))}
          </Box>

          {/* Desktop Nav Links */}
          <Box
            component="ul"
            sx={{
              display: { xs: "none", md: "flex" },
              gap: "2.5rem",
              listStyle: "none",
              margin: 0,
              padding: 0,
            }}
          >
            {navItems.map((item) => (
              <li key={item.label}>
                {item.type === "route" ? (
                  <Typography
                    component={Link}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    sx={{
                      color: "#2D3142",
                      textDecoration: "none",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 500,
                      transition: "color 0.3s",
                      cursor: "pointer",
                      "&:hover": { color: "#2e9e53" },
                    }}
                  >
                    {item.label}
                  </Typography>
                ) : (
                  <Typography
                    component="a"
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(item.href);
                    }}
                    sx={{
                      color: "#2D3142",
                      textDecoration: "none",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 500,
                      transition: "color 0.3s",
                      cursor: "pointer",
                      "&:hover": { color: "#2e9e53" },
                    }}
                  >
                    {item.label}
                  </Typography>
                )}
              </li>
            ))}
          </Box>

          {/* Desktop CTA */}
          <Typography
            component={Link}
            to="/login"
            sx={{
              display: { xs: "none", md: "inline-block" },
              background: "#0B4064",
              color: "white",
              padding: "0.4rem 1.8rem",
              borderRadius: "50px",
              textDecoration: "none",
              fontWeight: 700,
              transition: "all 0.3s",
              border: "2px solid #0B4064",
              cursor: "pointer",
              "&:hover": {
                background: "#184464",
                border: "2px solid #0b4064",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 20px rgba(11, 64, 100, 0.3)",
              },
            }}
          >
            Login
            {/* Start Free Trial */}
          </Typography>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "100vh",
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(15px)",
            boxShadow: "-10px 0 30px rgba(0,0,0,0.1)",
            overflowX: "hidden",
          },
        }}
      >
        {/* Close button — top right */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: "1.2rem 1.5rem",
          }}
        >
          <IconButton
            onClick={() => setMobileMenuOpen(false)}
            sx={{
              color: "#1A1A2E",
              border: "2px solid #1A1A2E",
              borderRadius: "50%",
              p: "6px",
              "&:hover": {
                backgroundColor: "#1A1A2E",
                color: "white",
              },
              transition: "all 0.2s",
            }}
          >
            <CloseIcon sx={{ fontSize: "1.4rem" }} />
          </IconButton>
        </Box>

        {/* Nav Items */}
        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            height: "calc(100vh - 80px)",
            padding: 0,
          }}
        >
          {navItems.map((item) => (
            <ListItem
              key={item.label}
              sx={{ justifyContent: "center", padding: "1.5rem 0" }}
            >
              {item.type === "route" ? (
                <Typography
                  component={Link}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#2D3142",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": { color: "#2e9e53" },
                    transition: "color 0.2s",
                  }}
                >
                  {item.label}
                </Typography>
              ) : (
                <Typography
                  component="a"
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.href);
                  }}
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#2D3142",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": { color: "#2e9e53" },
                    transition: "color 0.2s",
                  }}
                >
                  {item.label}
                </Typography>
              )}
            </ListItem>
          ))}

          {/* CTA Button */}
          <ListItem sx={{ marginTop: "1.5rem", justifyContent: "center" }}>
            <Typography
              component={Link}
              to="/login"
              sx={{
                background: "#0B4064",
                color: "white",
                padding: "0.4rem 1.8rem",
                borderRadius: "50px",
                textDecoration: "none",
                fontWeight: 700,
                fontSize: "1.1rem",
                border: "2px solid #0B4064",
                display: "inline-block",
                cursor: "pointer",
                "&:hover": {
                  background: "#2e9e53",
                  border: "2px solid #2e9e53",
                },
                transition: "all 0.2s",
              }}
            >
              Login
              {/* Start Free Trial */}
            </Typography>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Header;
